import React, { useCallback, useEffect, useMemo } from 'react';

// DEPENDENCIES
import queryString from 'query-string';
import { compose } from 'redux';
import { useFormikContext } from 'formik';
import { isString } from 'lodash';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';
import { NEW_USED_TYPES, PRODUCT_TYPES } from 'globals.js';
import { NEW_USED } from 'codes';

// GLOBAL HELPERS
import { withRouter } from 'helpers/withRouter';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';
import Record from 'parts/record/Record';

// MAIN COMPONENT
const ProductUpdate = compose(withRouter)(({ location: { search } }) => {
  // CONTEXT
  const { isAdd } = useRecordView();

  const newUsedParam = queryString.parse(search)?.new_used;
  const productTypeParam = queryString.parse(search)?.product_type;

  const newUsed = useMemo(() => {
    if (newUsedParam?.length > 1) {
      return newUsedParam.split(',').map(val => Number(val));
    }
    return [Number(newUsedParam)];
  }, [newUsedParam]);

  // RENDER
  return (
    <Record.Update
      title={`${isAdd ? 'Add' : 'Edit'} AM/Finance Product`}
      recordLabel="AM/Finance product"
      endpoints={ENDPOINTS.admin.amFinance.products}
      initialValues={{
        new_used: isAdd ? newUsed : undefined,
        product_type: isAdd ? parseInt(productTypeParam) : undefined,
        is_active: 1,
        add_product_cost: 0,
        lock_cost: 0
      }}
      formatRecord={record => ({
        ...record,
        new_used: !isAdd && [record?.new_used]
      })}
    >
      <Card body={<FormBody />} />
    </Record.Update>
  );
});

const FormBody = () => {
  // CONTEXT
  const { values, setFieldValue } = useFormikContext() || {};
  const { isEdit } = useRecordView();
  const { new_used, product_type, add_product_cost, sale_type_category_ids, product_min_gross, product_max_gross } =
    values || {};

  const newUsedLabel = useMemo(() => {
    if (!isEdit) {
      if (new_used?.length === 2) {
        return 'New, Pre-Owned';
      }
      if (new_used?.[0]) {
        return NEW_USED.new.label;
      }
      return NEW_USED.used.label;
    } else return NEW_USED_TYPES[new_used];
  }, [new_used, isEdit]);


  useEffect(() => {
    if (isEdit) {
      if (isString(sale_type_category_ids)) {
        setFieldValue('sale_type_category_id', sale_type_category_ids?.split(','));
      }
      if (isString(product_type)) {
        setFieldValue('product_type', PRODUCT_TYPES.indexOf(product_type));
      }
      return;
    }
  }, [sale_type_category_ids, product_type, isEdit, setFieldValue]);

  const handleChange = useCallback(
    ({ name, value }) => {
      setFieldValue(name, Number(value));
    },
    [setFieldValue]
  );

  return (
    <Form.Body>
      <Form.PlainText label="Type" value={newUsedLabel} />
      <Form.YesNo name="is_active" label="Active" required numeric onValueChange={handleChange} />
      <Form.Control name="product_code" label="Product Code" required />
      <Form.Control name="product_title" label="Product Title" required />
      <Form.Checklist
        label="Sale Type"
        name="sale_type_category_id"
        lookup="salesTypeCategory"
        optionKeys={{
          label: 'sale_type_category_name',
          value: 'sale_type_category_id'
        }}
        required
        useNone
      />
      <Form.PlainText label="Product Type" value={PRODUCT_TYPES[product_type]} />
      <Form.YesNo name="add_product_cost" label="Add Product Cost?" required onValueChange={handleChange} />
      {!!parseInt(add_product_cost) ? (
        <>
          <Form.Control name="product_cost" label="Product Cost?" type="number" required />
          <Form.Checklist name="lock_cost" label=" " options={[{ label: 'Lock Cost', value: 1 }]} />
        </>
      ) : null}
       <Form.Number name="product_min_gross" label="Minimum Gross" required max={product_max_gross} />
      <Form.Number name="product_max_gross" label="Maximum Gross" required min={product_min_gross} />
      <Form.Textarea name="product_description" label="Description" />
      <Record.Log />
    </Form.Body>
  );
};

// EXPORT
export default ProductUpdate;
